import { useSiteDates } from '@/components/checkin/deskRules';
import { useState } from 'react';
import {
  LogOut,
  ChevronsUpDown,
  Settings,
  ArrowLeftRight,
  Sun,
  Moon,
  MonitorSmartphone,
} from 'lucide-react';
import {
  useTranslation,
  useAuthStore,
  useLogout,
  useVersion,
  useUiStore,
  i18n as sharedI18n,
  normalizeLocale,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
} from '@bms/common';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useCardTapOpenSearch } from '@/lib/cardTapPreferences';
import { useTheme, type ThemePreference } from '@/providers/ThemeProvider';

const THEME_OPTIONS: { value: ThemePreference; icon: typeof Sun }[] = [
  { value: 'light', icon: Sun },
  { value: 'dark', icon: Moon },
  { value: 'system', icon: MonitorSmartphone },
];


// ============================================================================
// NavUser — Sidebar footer dropdown matching shadcn sidebar-icon-example
// Profile label → settings / admin switch → logout
// ============================================================================

/** Roles allowed to access the Admin UI */
const ADMIN_ROLES = new Set(['superadmin', 'site_admin']);

/**
 * A technician's readout, not a user-facing one: quiet enough that nobody
 * else notices it, findable by anyone who goes looking.
 *
 * It shows the build's Git description and nothing else. It used to lead with
 * `Edge {apiVersion}`, but GET /api/version hard-codes apiVersion to '1.0.0'
 * (src/routes/site.ts) — that names the REST contract BMS-spec requires and
 * never moves with a release, so the most prominent thing on the line was the
 * one thing that could not identify the build. The description is baked in at
 * build time (see vite.config.ts), so this never blocks on the network; the
 * probe now only enriches the tooltip.
 */
function VersionLine() {
  const { dateTime: siteDateTime } = useSiteDates();
  const { t } = useTranslation();
  const { data: version } = useVersion();

  const title = [
    `UI ${__UI_VERSION__}`,
    `build ${__UI_BUILD_SHA__}`,
    `built ${siteDateTime(__UI_BUILD_TIME__)}`,
    version?.apiVersion && `API ${version.apiVersion}`,
    version?.minUiVersion && `min UI ${version.minUiVersion}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <p
      className="px-2 pb-1 text-micro numeric text-muted-foreground/70 select-none group-data-[collapsible=icon]:hidden"
      title={title}
      aria-label={t('shell.edgeVersion')}
    >
      {__UI_BUILD_REF__}
    </p>
  );
}

export function NavUser() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const authLogout = useAuthStore((state) => state.logout);
  const logout = useLogout();
  const { isMobile } = useSidebar();
  const cardTapOpenSearch = useCardTapOpenSearch();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const displayName = user?.displayName || user?.username || 'User';
  const email = user?.username || '';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const canAccessAdmin = !!user?.role && ADMIN_ROLES.has(user.role);

  const handleLogout = () => {
    logout.mutate();
    authLogout();
    window.location.href = '/login';
  };

  const handleSwitchToAdmin = () => {
    window.location.href = '/admin';
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-caption font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-ui leading-tight">
                  <span className="truncate font-semibold">{displayName}</span>
                  <span className="truncate text-caption text-muted-foreground">
                    {email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'top'}
              align="end"
              sideOffset={4}
            >
              {/* Profile block */}
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-ui">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-caption font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-ui leading-tight">
                    <span className="truncate font-semibold">{displayName}</span>
                    <span className="truncate text-caption text-muted-foreground">
                      {email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Menu items */}
              <DropdownMenuGroup>
                <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuCheckboxItem
                  checked={cardTapOpenSearch.enabled}
                  onCheckedChange={(checked) => cardTapOpenSearch.setEnabled(checked === true)}
                  onSelect={(event) => event.preventDefault()}
                >
                  {t('nav.cardTapOpenSearch')}
                </DropdownMenuCheckboxItem>
                {canAccessAdmin && (
                  <DropdownMenuItem onClick={handleSwitchToAdmin}>
                    <ArrowLeftRight className="mr-2 h-4 w-4" />
                    {t('nav.switchToAdmin')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />

              {/* Logout */}
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <VersionLine />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        cardTapOpenSearch={cardTapOpenSearch}
      />
    </>
  );
}

// ============================================================================
// SettingsDialog — the direct picker behind the sidebar's dead Settings item.
// Keeps the same three-state theme preference and label set as the header's
// cycle button (T1/T5) rather than reinventing them, adds the locale switch
// already live in the Admin UI (packages/admin/src/layouts/MainLayout.tsx),
// and reuses the existing card-tap auto-navigate preference.
// ============================================================================

function SettingsDialog({
  open,
  onOpenChange,
  cardTapOpenSearch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardTapOpenSearch: ReturnType<typeof useCardTapOpenSearch>;
}) {
  const { t } = useTranslation();
  const { preference, setPreference } = useTheme();
  const locale = useUiStore((state) => state.locale);
  const setLocale = useUiStore((state) => state.setLocale);

  const handleLocaleChange = (next: string) => {
    setLocale(next);
    void sharedI18n.changeLanguage(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('nav.settings')}</DialogTitle>
          <DialogDescription>
            {t('settings.deviceDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <span className="text-ui font-medium">{t('settings.appearance')}</span>
            <div role="radiogroup" aria-label={t('settings.appearance')} className="flex gap-1.5">
              {THEME_OPTIONS.map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={preference === value}
                  onClick={() => setPreference(value)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-caption font-medium transition-colors ${
                    preference === value
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-accent'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {t(`shell.themeOption.${value}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <span className="text-ui font-medium">{t('settings.language')}</span>
            <div role="radiogroup" aria-label={t('settings.language')} className="flex gap-1.5">
              {SUPPORTED_LOCALES.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={normalizeLocale(locale) === value}
                  onClick={() => handleLocaleChange(value)}
                  className={`flex-1 rounded-md border px-2 py-2 text-caption font-medium transition-colors ${
                    normalizeLocale(locale) === value
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {LOCALE_LABELS[value]}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5 text-ui">
            {t('nav.cardTapOpenSearch')}
            <input
              type="checkbox"
              checked={cardTapOpenSearch.enabled}
              onChange={(event) => cardTapOpenSearch.setEnabled(event.target.checked)}
              className="h-4 w-4 accent-primary"
            />
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}
